import { checkBets } from '../../../api/betting-service-api';
import { CheckBetsRequest, CheckBetsResponse } from '../../../api/betting-service/betting-service-types';

const DELAY = 500;
const ABORT_ERROR_NAME = 'AbortError';
const CANCELLED_ERROR_NAME = 'Cancelled';

type EnqueuedRequest = {
	request: CheckBetsRequest;
	resolve: (value: CheckBetsResponse | PromiseLike<CheckBetsResponse>) => void;
	reject: (reason: unknown) => void;
	abortController: AbortController | undefined;
};

const createCancelledError = () => {
	const error = new Error('CancelledError');
	error.name = CANCELLED_ERROR_NAME;
	return error;
};

/**
 * Checks wether an error is a cancelled error created by the debouncing.
 */
export const isCancelledError = (error: unknown) => error instanceof Error && error.name === CANCELLED_ERROR_NAME;

/**
 * Creates a cancellable debounced checkbets call
 */
const createDebouncedCheckBets = () => {
	// Timeout
	let timeout: NodeJS.Timeout | undefined;

	// Latest parameters/request, this could be expanded in to an array if this cancellable debouncement is generalized.
	let enqueuedRequest: EnqueuedRequest | undefined;

	/**
	 * Runs enqueued request and treats requests aborted through AbortController
	 */
	const run = () => {
		if (enqueuedRequest) {
			enqueuedRequest.abortController = new AbortController();
			const localEnqueuedRequest = enqueuedRequest;
			checkBets(enqueuedRequest.request, enqueuedRequest.abortController.signal)
				.then(response => {
					localEnqueuedRequest.resolve(response);
				})
				.catch(e => {
					if (e instanceof Error && e.name === ABORT_ERROR_NAME) {
						localEnqueuedRequest.reject(createCancelledError());
						return;
					}
					localEnqueuedRequest.reject(e);
				});
		}
	};

	/**
	 * Cancel anything debounced, open requests, and rejects open promises
	 */
	const cancel = () => {
		if (timeout) {
			clearTimeout(timeout);
			timeout = undefined;
		}
		if (enqueuedRequest) {
			if (enqueuedRequest.abortController) {
				enqueuedRequest.abortController.abort();
			} else {
				enqueuedRequest.reject(createCancelledError());
			}
			enqueuedRequest = undefined;
		}
	};

	/**
	 * Cancels all enqueued calls and enqueues or runs a new call
	 */
	const enqueue = (request: CheckBetsRequest): Promise<CheckBetsResponse> => {
		// Cancel anything ongoing, no matter if enqueued or running.
		const hasTimeout = !!timeout;
		cancel();

		// Create new promise
		const promise = new Promise<CheckBetsResponse>((resolve, reject) => {
			enqueuedRequest = {
				request,
				resolve,
				reject,
				abortController: undefined
			};
		});

		// Run immediately if we don't have a timeout
		if (!hasTimeout) {
			run();
			timeout = setTimeout(() => {
				timeout = undefined;
			}, DELAY);
			return promise;
		}
		// Otherwise just set a timeout to run it at some later point
		timeout = setTimeout(() => {
			run();
			timeout = undefined;
		}, DELAY);
		return promise;
	};

	enqueue.cancel = cancel;
	return enqueue;
};

export default createDebouncedCheckBets;
